// @ts-nocheck

import * as React from "react"
import { AutoSizer, CellMeasurer, CellMeasurerCache, IndexRange, InfiniteLoader, List, ListRowProps } from "react-virtualized"
import { ListItemFactory } from "./ListItemFactory"
import { EntityBasicData } from "../EntityType"
import ReactPlaceholder from "react-placeholder"
import "react-placeholder/lib/reactPlaceholder.css"
import { ListDeleteCallback, ListMessageCallback, ListReloadCallback } from "./MobileListDataWrapper"
import { Alert, Snackbar } from "@mui/material"
import { AlertColor } from "@mui/material/Alert"

export type MobileListViewProps = {
	table: string,
	listData: EntityBasicData[]

	handleFetchFeed: ( range: IndexRange ) => Promise<void>,
	deleteCallback: ListDeleteCallback
	reloadCallback?: ListReloadCallback
}

export const REQUEST_THRESHOLD = 15

let lastRequestedIndex: number

export function MobileListView( props: MobileListViewProps ) {

	const [ alertMessage, setAlertMessage ] = React.useState( undefined )
	const [ alertSeverity, setAlertSeverity ] = React.useState<AlertColor>( 'success' )
	const [ showLoading, setShowLoading ] = React.useState( false )

	const cellMeasurerCache: CellMeasurerCache = new CellMeasurerCache( {
		fixedWidth: true,
		defaultHeight: 80,
	} )

	const isRowLoaded = ( index: number ): boolean => {
		return props.listData[ index ] !== undefined
	}

	const handleClose = ( _event?: React.SyntheticEvent | Event, reason?: string ) => {
		if( reason === "clickaway" ) {
			return
		}

		setAlertMessage( undefined )
	}

	const listMessageCallback = ( message: string, type?: AlertColor ) => {
		setAlertMessage( message )
		setAlertSeverity( type ?? 'success' )
	}

	return <>
		<div className="InfinteList" style={ { height: "100vh" } }>

			<ReactPlaceholder
				ready={ props.listData.length > 0 && showLoading === false }
				type={ "text" }
				rows={ REQUEST_THRESHOLD }
				showLoadingAnimation={ true }
			>
				<InfiniteLoader
					minimumBatchSize={ REQUEST_THRESHOLD }
					isRowLoaded={ ( index ) => isRowLoaded( index.index ) }
					loadMoreRows={ ( range: IndexRange ): Promise<unknown> => {
						if( props.listData.length > 0 && props.listData.length <= ( REQUEST_THRESHOLD / 2 ) ) {
							return Promise.resolve()
						}

						if( lastRequestedIndex != null && ( range.startIndex - lastRequestedIndex ) < ( REQUEST_THRESHOLD / 2 ) ) {
							console.log( "request blocked" )
							return Promise.resolve()
						}

						lastRequestedIndex = range.startIndex

						return props.handleFetchFeed( range )
					} }
					rowCount={ 10000000 }
					// threshold={ REQUEST_THRESHOLD }
				>
					{ ( { onRowsRendered } ) => (
						<AutoSizer>
							{ ( { width, height } ) => (
								<List
									rowCount={ props.listData.length }
									width={ width }
									height={ height }
									rowHeight={ cellMeasurerCache.rowHeight }
									rowRenderer={ ( listRowProps: ListRowProps ) => {
										return <RowRenderer
											key={ listRowProps.index }
											cellMeasurerCache={ cellMeasurerCache }
											listData={ props.listData }
											listRowProps={ listRowProps }
											onDeleteItemCallback={ props.deleteCallback }
											onEditItemCallback={ props.reloadCallback }
											onListMessageCallback={ listMessageCallback }
											onLoadingStart={ setShowLoading }
											table={ props.table }
										/>
									}
									}
									deferredMeasurementCache={ cellMeasurerCache }
									// overscanRowCount={ 8 }
									onRowsRendered={ onRowsRendered }
								/>
							) }
						</AutoSizer>
					) }
				</InfiniteLoader>
			</ReactPlaceholder>
		</div>

		<Snackbar open={ alertMessage !== undefined } autoHideDuration={ alertSeverity === 'success' ? 5000 : 8000 } onClose={ handleClose }
				  anchorOrigin={ { horizontal: "center", vertical: "bottom" } }>

			<Alert severity={ alertSeverity } sx={ { width: "100%", fontSize: "1rem" } } onClose={ handleClose }>
				{ alertMessage }
			</Alert>
		</Snackbar>
	</>
}

type RowRendererProps = {
	listRowProps: ListRowProps,

	table: string
	listData: EntityBasicData[],
	cellMeasurerCache: CellMeasurerCache,

	onDeleteItemCallback: ListDeleteCallback,
	onEditItemCallback?: ListEditCallback,
	onListMessageCallback?: ListMessageCallback,
};

function RowRenderer( props: RowRendererProps ) {

	const entityData = props.listData[ props.listRowProps.index ]
	return <CellMeasurer
		key={ props.listRowProps.key }
		cache={ props.cellMeasurerCache }
		parent={ props.listRowProps.parent }
		columnIndex={ 0 }
		rowIndex={ props.listRowProps.index }
	>
		<ListItemFactory
			data={ entityData }
			table={ entityData.entityTable ?? props.table }
			style={ props.listRowProps.style }
			index={ props.listRowProps.index }
			onDeleteItemCallback={ props.onDeleteItemCallback }
			onEditItemCallback={ props.onEditItemCallback }
			onListMessageCallback={ props.onListMessageCallback }
			onLoadingStart={ props.onLoadingStart }
		/>
	</CellMeasurer>
}